// Keep shipping updates and payment confirmation in the same Checkout Form
// session. In particular, never confirm a payment with an outdated rate.
export const mountCheckoutForm = async ({
  stripe,
  clientSecret,
  container,
  updateShippingOptions,
  onReady,
  onError,
  onShippingUpdated = () => {},
  isCancelled = () => false,
}) => {
  if (!stripe) {
    throw new Error("Unable to load secure checkout. Please try again.");
  }

  const checkout = stripe.initCheckoutFormSdk({ clientSecret });
  const result = await checkout.loadActions();
  if (result.type !== "success") {
    throw new Error(result.error.message);
  }
  if (isCancelled()) return null;

  const { actions } = result;
  const form = checkout.createForm();
  let destroyed = false;
  let confirming = false;
  let shippingKey = null;
  let shippingUpdate = Promise.resolve();

  const updateShipping = (shippingDetails) => {
    const key = JSON.stringify(shippingDetails);
    if (key === shippingKey) return shippingUpdate;
    shippingKey = key;

    // Serialize address edits so a slow response for an earlier address
    // cannot replace the shipping rate for the latest address.
    shippingUpdate = shippingUpdate
      .catch(() => {})
      .then(async () => {
        if (destroyed) return;
        const update = await actions.runServerUpdate(() =>
          updateShippingOptions(actions.getSession().id, shippingDetails),
        );
        if (update.type === "error") {
          throw new Error(update.error.message);
        }
        if (!destroyed) onShippingUpdated();
      });
    shippingUpdate.catch(() => {
      if (shippingKey === key) shippingKey = null;
    });
    return shippingUpdate;
  };

  const reportError = (error) => {
    if (!destroyed) onError(error);
  };

  form.on("ready", onReady);
  form.on("loaderror", ({ error }) => reportError(error));
  form.on("change", ({ value, status }) => {
    if (destroyed) return;
    if (!status.shippingAddress?.complete) {
      shippingKey = null;
      return;
    }
    updateShipping(value.shippingAddress).catch(reportError);
  });
  form.on("confirm", async (event) => {
    if (destroyed || confirming) return;
    confirming = true;
    try {
      // An address can change while an earlier rate is still loading. Keep
      // checking until the current form address and the last update agree.
      while (!destroyed) {
        const { value, status } = await form.getValue();
        if (!status.shippingAddress?.complete) {
          throw new Error("Please complete your shipping address.");
        }
        const key = JSON.stringify(value.shippingAddress);
        const pending = updateShipping(value.shippingAddress);
        await pending;
        const latest = await form.getValue();
        if (
          latest.status.shippingAddress?.complete &&
          JSON.stringify(latest.value.shippingAddress) === key &&
          pending === shippingUpdate
        ) {
          break;
        }
      }
      if (destroyed) return;
      const confirmation = await actions.confirm({
        formConfirmEvent: event,
        // The return URL is set on the server-created Checkout Session.
        // Stripe rejects confirmation if it is supplied a second time here.
        redirect: "always",
      });
      if (confirmation.type === "error") {
        throw new Error(confirmation.error.message);
      }
    } catch (error) {
      reportError(error);
    } finally {
      confirming = false;
    }
  });

  try {
    form.mount(container);
  } catch (error) {
    form.destroy();
    throw error;
  }

  return {
    destroy: () => {
      destroyed = true;
      form.destroy();
    },
  };
};
